import 'moment/locale/ko';

import autobind from 'autobind-decorator';
import moment from 'moment-timezone';
import { equals, map, pipe } from 'ramda';

type Moment = moment.Moment;

interface IYaMoment {
  getSeoulTime(date?: string): Moment;
  getYaDayDuration(startDate?: string, endDate?: string): [Moment, Moment];
  getNextDay(time: Moment): Moment;
  formatMoment(format: string): (time: Moment) => string;
  parseToMoment(dateLike: string | Moment): Moment;
  getDateFormat(time: Moment): string;
  getDateTimeFormat(time: Moment): string;
  isBetweenYaDuration(targetDate: string): boolean;
  isSameDurationOfToday(targetDate: string): boolean;
}

class YaMoment implements IYaMoment {
  /**
   * 메서도 호출 당시 현재 시간을 KST 시간대로 가져옴
   *
   * @param date string YYYY-MM-DD
   */
  @autobind
  getSeoulTime(date?: string): Moment {
    return moment(date).tz('Asia/Seoul');
  }

  /**
   * 현재 시간을 초단위로 반환
   *
   * @param date string YYYY-MM-DD
   */
  @autobind
  getSeoulTimeUnix(date?: string): number {
    return moment(date).tz('Asia/Seoul').unix();
  }

  /**
   * 익일 새벽 2시까지 당일로 잡는 야놀자타임을 구함 (실제로는 01:59:59까지 계산할 것)
   *
   * @param startDate string YYYY-MM-DD
   * @param endDate string YYYY-MM-DD
   * @return tuple of moment instances
   *
   * 1. 인자가 없을 경우 - 현재 시간을 구해서 그 시간대의 하루 Duration을 구한다
   *   ex) 현재 시간이 2018년 5월 30일 15시일 경우 결과값은 [2018년 5월 30일 02:00, 2018년 5월 31일 01:59:59]
   * 2. 인자가 없으나 현재 시간이 특정일의 00:00시에서 01:59:59시 사이일 경우
   *   ex) 현재 시간이 2018년 5월 30일 01시일 경우 결과값은 [2018년 5월 29일 02:00, 2018년 5월 30일 01:59:59]
   * 3. 시작일과 종료일이 제시된 경우 - 시작일의 새벽 2시부터 종료일의 23:59:59까지 리턴
   *   ex) startDate - 2018-04-30, endDate - 2018-06-02 일 경우 결과값은 [2018-04-30 02:00, 2018-06-02 23:59:59]
   */
  @autobind
  getYaDayDuration(startDate?: string, endDate?: string): [Moment, Moment] {
    if (startDate && endDate) {
      return [this.getSeoulTime(startDate).add(2, 'hours'), this.getSeoulTime(endDate).endOf('day')];
    }

    // Testability 강화. 만약 startDate가 들어오지 않았다면 자동적으로 메서드 호출 시점의 시간 추출
    const currentTime = this.getSeoulTime(startDate);
    const startTime = this.setTimeToZero(currentTime);
    const endTime = startTime.clone();

    if (this.isInYaTime(currentTime)) {
      return [startTime.subtract(1, 'day').add(2, 'hours'), endTime.add(2, 'hours').subtract(1, 'second')];
    }

    return [startTime.add(2, 'hours'), this.nextYaDateTime(endTime)];
  }

  @autobind
  getNextDay(time: Moment): Moment {
    return time.clone().add(1, 'day');
  }

  /**
   * 현재 시간이 지정한 날짜의 야놀자타임 범위 안에 포함되는지 확인
   * 만약 날짜를 지정하지 않으면 현재 시간 기준 야놀자타임을 구해서 확인
   * @param targetDate string YYYY-MM-DD
   */
  @autobind
  isBetweenYaDuration(targetDate?: string): boolean {
    const targetDuration = targetDate ? this.getYaDayDuration(targetDate) : this.getYaDayDuration();

    return this.getSeoulTime().isBetween(targetDuration[0], targetDuration[1]);
  }

  /**
   * 인자로 넘긴 날짜의 야놀자타임 범위와 현재 시간 기준 야놀자타임의 범위가 일치하는지 확인
   * @param targetDate string YYYY-MM-DD
   */
  @autobind
  isSameDurationOfToday(targetDate: string): boolean {
    const targetDuration = this.getYaDayDuration(targetDate);
    const currentYaDuration = this.getYaDayDuration();
    const mapDateTimeFormat = map(this.getDateTimeFormat);

    return equals(mapDateTimeFormat(targetDuration), mapDateTimeFormat(currentYaDuration));
  }

  @autobind
  getDateFormat(time: Moment): string {
    return this.formatMoment('YYYY-MM-DD')(time);
  }

  @autobind
  getDateTimeFormat(time: Moment): string {
    return this.formatMoment('YYYY-MM-DD hh:mm:ss')(time);
  }

  @autobind
  formatMoment(format: string) {
    return function byFormat(time: Moment) {
      return time.format(format);
    };
  }

  @autobind
  parseToMoment(dateLike: string | Moment): Moment {
    return typeof dateLike === 'string' ? this.getSeoulTime(dateLike) : dateLike;
  }

  @autobind
  convertDurationDays(duration?: string): number | undefined {
    return duration ? moment.duration(duration).asDays() : undefined;
  }

  /** 표기 날짜의 전날로 구분해야하는지 확인하기 위한 체킹 (00:00:00 ~ 01:59:59) */
  @autobind
  private isInYaTime(time: Moment): boolean {
    const currentDate = this.getDateFormat(time);

    return time.isBetween(`${currentDate} 00:00:00`, `${currentDate} 01:59:59`);
  }

  /** 익일 01:59:59 로 설정된 moment 객체를 리턴 */
  @autobind
  private nextYaDateTime(time: Moment): Moment {
    return this.setTimeToZero(time).add(1, 'days').add(2, 'hours').subtract(1, 'second');
  }

  /**
   * 현재 시간 등 시간이 붙아있는 moment 객체의 시간을 제거한다.
   * 해당 시간의 00:00:00 이 설정된 새 moment 객체 리턴
   */
  @autobind
  private setTimeToZero(time: Moment): Moment {
    return pipe(this.getDateFormat, this.getSeoulTime)(time.clone());
  }
}

export const YaMo = new YaMoment();

export default YaMoment;
